import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { isNil, isNumber, random, cloneDeep } from 'lodash';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  MultiAgentPickerComponent,
  REMOVED
} from '@nrwl/test-setup';
import {
  UnmanagedDevicesService,
  TestListService,
  UserService,
  CommonRegEx,
  TwampManagedDevicesService,
  Device,
  DevicesService,
  TransformationService,
  AGENT_TYPE,
  AGENT_MODEL,
  TestMode,
  IPsService,
  TestListEntity,
  TEST_STATE
} from '@nrwl/common-lib';
import { TwampTestService } from '../../../twamp-test.service';
import {
  ETHER_TYPE,
  IPAddressValue,
  TRANSMIT_INTERFACE_TYPE,
  TRANSMIT_INTERFACE_VALUE,
  TX_IMAGE,
  DEFINED_ADDRESS_TYPES,
  definedAddressValue,
  TwampConfigFieldMapping
} from '../../models/model';

@Component({
  selector: 'nrwl-twamp-test',
  templateUrl: './twamp-test.component.html',
  styleUrls: ['./twamp-test.component.scss']
})
export class TwampTestComponent implements OnInit, OnDestroy {
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onOtherTabDataValueChange = new EventEmitter<IValueChangedEvent>();
  @Input() stateHistory?: IComponentStateHistory;
  @Input() otherTabStateHistory: IComponentStateHistory;
  @ViewChild('currentForm', { static: true }) form: NgForm;
  @ViewChild('thresholdForm', { static: true }) thresholdForm: NgForm;
  // eslint-disable-next-line
  destinationValue: BehaviorSubject<any>;
  // eslint-disable-next-line
  originatorValue: BehaviorSubject<any>;
  config: any;
  //eslint-disable-next-line
  @Input() componentId: string;
  @Input() tab: BaseSetupTab;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreAgentBasedConfig?: Function;
  //eslint-disable-next-line
  validIPV4V6RegEx: RegExp = CommonRegEx.IP_V4_V6;
  originatorDialogRef: MatDialogRef<any>;
  //eslint-disable-next-line
  destinationdialogRef: MatDialogRef<any>;
  outputs: Array<string>;
  agentType = null;
  etherTypesData = ETHER_TYPE;
  testMode = TestMode.DEFAULT;
  testType = 'twamp-initiator';
  txInterfaceTypes = TRANSMIT_INTERFACE_TYPE;
  txTypeImage = TX_IMAGE.EQUIPMENT;
  definedAddressTypes = [...DEFINED_ADDRESS_TYPES];
  configMapping = TwampConfigFieldMapping;
  maxControlSessionCount = null;
  defaultControlSessionCount = 1300;
  ipFields = {
    assignedIpAddress: null,
    testIpAddress: null,
    subnet: null,
    gateway: null
  };
  formField = {
    txInterface: null,
    ipAddrType: null,
    definedAddress: null,
    macAddress: null,
    equipmentMac: null,
    networkMac: null,
    initiatorIp: '',
    subnet: '',
    gatewayIp: '',
    senderUdpPort: 50000,
    vlanConfig: null,
    svlan: null,
    svlanPriority: null,
    cvlan: null,
    cvlanPriority: null,
    cvlanEtherType: null,
    isSubnetValid: true,
    isAddressValid: true,
    isGatewayValid: true
  };
  vlanConfigTypes = [];
  selectedOriginator: Device;
  selectedDestination = [];
  selectedReflectorIds = [];
  originatorInfo = {
    title: '',
    label: '',
    subLabel: ''
  };
  destinationInfo = {
    title: '',
    label: '',
    subLabel: ''
  };
  destinationConfiguration = {
    title: 'Destination (reflector)*',
    label: '',
    message: 'Choose the second endpoint for the test',
    icon: 'mdi mdi-map-marker'
  };
  nameExist: boolean;
  testName: string;
  allTestNames: Array<string>;
  newTestMode = true;
  editMode = false;
  cloneMode = false;
  cacheObj: Subscription;
  deviceCache: Subscription;
  actualTestName: string;
  unManagedDevices = [];
  nameDisabled = false;
  badIpValues = ['', '0', '0.0.0.0', '255.255.255.255', '::/0'];
  private readonly _unsubscribe: Subject<void> = new Subject();
  managedDevices: Device[];
  twampRunningDevices: Device[];
  agentFlowInfo: any = {};
  commonFlows = [];
  selectedIndex = 0;
  ipAddressTypes = [];
  //eslint-disable-next-line
  constructor(
    private dialog: MatDialog,
    private testListService: TestListService,
    private twampTestService: TwampTestService,
    private userService: UserService,
    private readonly unManagedDeviceService: UnmanagedDevicesService,
    private readonly twampDeviceService: TwampManagedDevicesService,
    private readonly deviceService: DevicesService,
    private transformationService: TransformationService,
    private readonly ipService: IPsService
  ) {
    // eslint-disable-next-line
    this.destinationValue = new BehaviorSubject<any>([]);
    // eslint-disable-next-line
    this.originatorValue = new BehaviorSubject<any>([]);
  }

  ngOnInit(): void {
    this.cacheObj = this.unManagedDeviceService.cache.subscribe(devices => {
      this.unManagedDevices = devices;
    });
    this.deviceCache = this.deviceService.cache.subscribe(res => {
      this.managedDevices = res;
    });
    this.deviceCache = this.twampDeviceService.cache.subscribe((twampManagedDevices: any) => {
      this.twampRunningDevices = twampManagedDevices;
    });
    this.initilizeData();
    this.cacheObj = this.testListService.cache.subscribe(data => {
      this.allTestNames = data.map(item => item.escapedName);
      if (this.testName) {
        this.checkIfNameAlreadyExist();
      }
    });
  }

  initilizeData(): void {
    this.restoreAgentBasedConfig();
    this.restoreFormValues();
    this.restoreOtherTabValues();
    this.newTestMode = this.twampTestService.mode === 'new';
    this.editMode = this.twampTestService.editMode;
    this.cloneMode = this.twampTestService.mode === 'clone';
    if (!this.newTestMode) {
      this.setOriginator();
    }
    if (!(this.editMode || this.cloneMode)) {
      this.testName = `${this.testName}-${this.userService.getUserName()}-${new Date().getTime()}`;
    }
    this.nameDisabled = false;
    if (this.editMode) {
      const scheduled = this.twampTestService.defaultParamsById['scheduleId'];
      if (scheduled) {
        const testEntity: TestListEntity = this.testListService.getTestListById(scheduled);
        if (
          testEntity.getTestStatus() === TEST_STATE.RUNNING ||
          testEntity.getTestStatus() === TEST_STATE.SCHEDULED
        ) {
          this.nameDisabled = true;
        }
      }
    }
  }

  restoreFormValues(): void {
    this.config = this.tab.config;
    this.tab.form = this.form;
    this.outputs = this.config.def.outputs;
    this.ipAddressTypes = this.config.options[TwampConfigFieldMapping.IP_ADDRESS_TYPE].value;
    this.testName = this.restoreFieldValues(TwampConfigFieldMapping.ALIAS);
    this.actualTestName = this.testName;
    this.vlanConfigTypes = this.config.options[TwampConfigFieldMapping.VLAN_CONFIG].value;
    this.selectedOriginator = this.restoreFieldValues(TwampConfigFieldMapping.SELECTED_ORIGINATOR);
    this.selectedDestination = this.restoreFieldValues(
      TwampConfigFieldMapping.SELECTED_DESTINATION
    );
    this.agentType = this.restoreFieldValues(TwampConfigFieldMapping.AGENT_TYPE);
    this.selectedReflectorIds = this.restoreFieldValues(
      TwampConfigFieldMapping.SELECTED_REFLECTOR_IDS
    );
    this.formField.txInterface = this.restoreFieldValues(
      TwampConfigFieldMapping.TRANSMIT_INTERFACE
    );
    this.formField.ipAddrType = this.restoreFieldValues(TwampConfigFieldMapping.IP_ADDRESS_TYPE);
    this.formField.macAddress = this.restoreFieldValues(TwampConfigFieldMapping.MAC_ADDRESS);
    this.formField.networkMac = this.restoreFieldValues(TwampConfigFieldMapping.NETWORK_MAC);
    this.formField.equipmentMac = this.restoreFieldValues(TwampConfigFieldMapping.EQUIPMENT_MAC);
    this.formField.definedAddress = this.restoreFieldValues(
      TwampConfigFieldMapping.DEFINED_ADDRESS
    );
    this.formField.initiatorIp = this.restoreFieldValues(TwampConfigFieldMapping.ADDRESS);
    this.formField.subnet = this.restoreFieldValues(TwampConfigFieldMapping.SUBNET);
    this.formField.gatewayIp = this.restoreFieldValues(TwampConfigFieldMapping.GATEWAY);
    this.formField.vlanConfig = this.restoreFieldValues(TwampConfigFieldMapping.VLAN_CONFIG);
    this.formField.senderUdpPort = this.restoreFieldValues(TwampConfigFieldMapping.BASE_UDP);
    this.formField.svlan = this.restoreFieldValues(TwampConfigFieldMapping.SVLAN_ID);
    this.formField.cvlan = this.restoreFieldValues(TwampConfigFieldMapping.CVLAN_ID);
    this.formField.svlanPriority = this.restoreFieldValues(TwampConfigFieldMapping.SVLAN_PRIORITY);
    this.formField.cvlanPriority = this.restoreFieldValues(TwampConfigFieldMapping.CVLAN_PRIORITY);
    this.formField.cvlanEtherType = this.restoreFieldValues(
      TwampConfigFieldMapping.CVLAN_ETHER_TYPE
    );
    this.getDataBasedOnAgentConfig();
    this.getOriginatorDestinationDetails();
  }

  restoreOtherTabValues(): void {
    this.commonFlows = this.restoreAdvancedFieldValues(TwampConfigFieldMapping.COMMON_FLOWS);
    this.agentFlowInfo = this.restoreAdvancedFieldValues(TwampConfigFieldMapping.SPECIFIC_FLOWS);
    this.selectedIndex = this.restoreAdvancedFieldValues(TwampConfigFieldMapping.SELECTED_TAB);
  }

  getOriginatorDestinationDetails(): any {
    this.twampTestService.setOriginatorAgent(this.selectedOriginator);
    if (this.selectedReflectorIds?.length) {
      const unManagedDevice =
        this.unManagedDevices.filter(
          item =>
            this.selectedReflectorIds.some(data => data.id === item.deviceData.id) &&
            item.deviceData?.['running-reflectors']?.length
        ) || [];
      const managedDevice =
        this.twampRunningDevices?.filter(
          item =>
            this.selectedReflectorIds.some(data => data.id === item.deviceData.id) &&
            item.deviceData?.['running-reflectors']?.length
        ) || [];
      const combinedDevice = [...unManagedDevice, ...managedDevice];
      this.getReflectorsData(combinedDevice);
    } else {
      //Test created from legacy UI or btf script to get device entity based on ipaddress
      const unManagedDevice =
        this.unManagedDevices.filter(item => item.deviceData?.['running-reflectors']?.length) || [];
      const managedDevice =
        this.twampRunningDevices?.filter(item => item.deviceData?.['running-reflectors']?.length) ||
        [];
      const combinedDevice = [...unManagedDevice, ...managedDevice];
      this.getReflectorsData(combinedDevice);
    }
    this.setDestination();
  }

  getReflectorsData(reflectors: Device[]): any {
    this.selectedDestination?.forEach(dest => {
      const selectedEntity = reflectors.find(entity =>
        entity.deviceData['running-reflectors']?.some(item => item.ip === dest.key)
      );
      if (selectedEntity) {
        const selectedReflector = selectedEntity.deviceData['running-reflectors'].find(
          item => item.ip === dest.key
        );
        dest['id'] = selectedEntity.getId();
        dest['name'] = {
          label: selectedEntity.deviceData?.deviceName ?? selectedEntity.deviceData.name
        };
        dest['dataInfo'] = this.transformationService.createDataInfo(
          selectedReflector,
          selectedEntity
        );
        dest['deviceData'] = selectedEntity.deviceData;
        dest['notAvailed'] = false;
      } else {
        dest['notAvailed'] = true;
        dest['errMsg'] = `Device does not match with inventory`;
      }
    });
  }

  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  //eslint-disable-next-line
  restoreAdvancedFieldValues(field: string): any {
    const data = this.otherTabStateHistory('twamp-test-advanced.component.html', field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  nameUpdated(): void {
    this.nameExist = false;
    this.onDataEmit(TwampConfigFieldMapping.ALIAS, null, false);
    if (this.testName) {
      const pattern = new RegExp('[?%/]');
      if (pattern.test(this.testName)) {
        this.form.controls.testName?.setErrors({ regex: 'wrong' });
        this.form.controls.testName?.markAsDirty();
      }
      this.checkIfNameAlreadyExist();
    }
  }
  checkIfNameAlreadyExist(): void {
    if (this.editMode) {
      this.allTestNames = this.allTestNames.filter(name => name !== this.actualTestName);
    }
    if (this.allTestNames.some(item => item.toLowerCase() === this.testName.toLowerCase())) {
      this.onDataEmit(TwampConfigFieldMapping.ALIAS, null, false);
      this.nameExist = true;
    } else {
      this.nameExist = false;
      this.onDataEmit(TwampConfigFieldMapping.ALIAS, this.testName, true);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onAgentChange(data: any): void {
    this.selectedOriginator = this.deviceService.findDeviceByID(data.originatorId);
    this.setOriginator();
  }

  viewOriginator(): void {
    let managedDevices = this.getSupportedDevices();
    if (this.selectedDestination?.length) {
      managedDevices = managedDevices.filter(
        item =>
          !this.selectedDestination.some(devices => item.deviceData.id === devices.deviceData.id)
      );
    }
    this.originatorDialogRef = this.dialog.open(MultiAgentPickerComponent, {
      width: '100rem',
      height: '66rem',
      disableClose: true,
      data: {
        originator: managedDevices,
        from: 'originator',
        singleSelection: true,
        persistenceID: 'originator-selection',
        testType: this.testType,
        cartView: false,
        havingChildRows: false,
        transformationString: 'twamp-originator-selection',
        pageConfigurations: {
          title: 'Select Originator endpoints',
          selectionType: 'endpoints'
        },
        preSelectedDevices: this.selectedOriginator ? [this.selectedOriginator?.deviceData] : []
      }
    });
    this.originatorDialogRef.afterClosed().subscribe(selectedRows => {
      if (selectedRows) {
        this.selectedOriginator = this.deviceService.findDeviceByID(selectedRows?.[0].id);
        this.setOriginator();
      }
    });
  }
  viewDestination(): void {
    let bothDevices = null;
    let filteredReflectors = null;
    const supportedUnmanagedDevice = this.unManagedDeviceService.getReflectorTypeDevices(
      this.unManagedDevices
    );
    const supportedDevice = this.getRunningReflectors();
    bothDevices = [...supportedUnmanagedDevice, ...supportedDevice];
    if (this.selectedOriginator) {
      bothDevices = bothDevices.filter(
        item => this.selectedOriginator.getId() !== item.deviceData.id
      );
      filteredReflectors = JSON.parse(JSON.stringify(bothDevices));

      //* JMEP Initiator to support only light reflectors */
      if (this.agentType === AGENT_MODEL.JMEP_1G && bothDevices.length) {
        const lightReflectors = [];
        for (let i = 0; i < filteredReflectors.length; i++) {
          const reflectors = filteredReflectors[i].deviceData['running-reflectors'];
          const fliterLight = reflectors.filter(item => item.type === 'twamp-light-reflector');
          if (fliterLight?.length > 0) {
            filteredReflectors[i].deviceData['running-reflectors'] = fliterLight;
            lightReflectors.push(filteredReflectors[i]);
          }
        }
        filteredReflectors = Object.assign([], lightReflectors);
      }
    }
    this.destinationdialogRef = this.dialog.open(MultiAgentPickerComponent, {
      width: '100rem',
      height: '66rem',
      disableClose: true,
      data: {
        persistenceID: 'destination-selection',
        from: 'destination',
        cartView: true,
        cartItems: this.selectedDestination,
        destination: filteredReflectors,
        havingChildRows: true,
        transformationString: 'twamp-destination-selection',
        pageConfigurations: {
          title: 'Select destination endpoints',
          selectionType: 'endpoints'
        },
        maxDeviceCount: parseInt(this.maxControlSessionCount.toString())
      }
    });
    this.destinationdialogRef.afterClosed().subscribe(selectedRows => {
      if (selectedRows) {
        this.selectedDestination = selectedRows;
        this.setDestination();
      }
    });
  }

  getSupportedDevices(): Device[] {
    const supportedDevice = [AGENT_MODEL.QT600, AGENT_MODEL.VPMA, AGENT_MODEL.VPMA_L];
    return this.managedDevices.filter(device =>
      supportedDevice.includes(device.deviceData.model.model)
    );
  }
  getRunningReflectors(): Device[] {
    const reflectors = this.twampRunningDevices.filter(
      item =>
        item.deviceData['running-reflectors'] && item.deviceData['running-reflectors'].length > 0
    );
    return reflectors;
  }
  setOriginator(): void {
    if (this.selectedOriginator) {
      this.agentType = this.selectedOriginator?.getModel();
      this.twampTestService.setOriginatorAgent(this.selectedOriginator);
      this.onDataEmit(TwampConfigFieldMapping.AGENT_TYPE, this.agentType, true);
      this.onDataEmit(TwampConfigFieldMapping.SELECTED_ORIGINATOR, this.selectedOriginator, true);
      this.restoreAgentBasedConfig(this.agentType);
      this.restoreFormValues();
      this.getDeviceMaxControlSessionCount();
    }
  }

  getDeviceMaxControlSessionCount(): void {
    const phyId = this.selectedOriginator.getDevice().phyId;
    if (phyId) {
      const portItem = this.selectedOriginator
        .getDevice()
        .capabilities['twamp-initiator-list']?.find(item => item['phy-id'] === phyId);
      if (portItem) {
        this.maxControlSessionCount =
          portItem['max-control-sessions-per-port'] || this.maxControlSessionCount;
      }
    } else {
      if (this.selectedOriginator.getModel().includes('JMEP')) {
        this.maxControlSessionCount = 10;
      } else {
        this.maxControlSessionCount =
          this.selectedOriginator.getDevice().capabilities?.['twamp-initiator']?.[
            'max-control-sessions-per-test'
          ] || this.defaultControlSessionCount;
      }
    }
  }

  showFormField(field: string): boolean {
    return this.outputs.includes(field);
  }

  getDataBasedOnAgentConfig(): void {
    if (this.agentType === AGENT_MODEL.JMEP_1G) {
      const capabilityData = this.twampTestService.getAgentLookUpData(
        this.config.lookup,
        this.selectedOriginator
      );
      this.ipFields.assignedIpAddress = capabilityData[TwampConfigFieldMapping.ASSIGNED_IP_ADDRESS];
      if (!this.badIpValues.includes(capabilityData[TwampConfigFieldMapping.ADDRESS])) {
        this.ipFields.testIpAddress = capabilityData[TwampConfigFieldMapping.ADDRESS];
      }
      if (!this.badIpValues.includes(capabilityData[TwampConfigFieldMapping.SUBNET])) {
        this.ipFields.subnet = capabilityData[TwampConfigFieldMapping.SUBNET]
          ? this.ipService.getSubnetByMapValue(capabilityData[TwampConfigFieldMapping.SUBNET])
          : this.formField.subnet;
      }
      if (!this.badIpValues.includes(capabilityData[TwampConfigFieldMapping.GATEWAY])) {
        this.ipFields.gateway = capabilityData[TwampConfigFieldMapping.GATEWAY];
      }
      this.formField.networkMac = capabilityData[TwampConfigFieldMapping.NETWORK_MAC];
      this.formField.equipmentMac = capabilityData[TwampConfigFieldMapping.EQUIPMENT_MAC];
      if (this.newTestMode) {
        this.formField.svlan =
          capabilityData[TwampConfigFieldMapping.SVLAN_ID] !== '-1'
            ? capabilityData[TwampConfigFieldMapping.SVLAN_ID]
            : '';
        this.onDataEmit(
          TwampConfigFieldMapping.ASSIGNED_IP_ADDRESS,
          this.ipFields.assignedIpAddress,
          true
        );
        this.onDataEmit(TwampConfigFieldMapping.SVLAN_ID, this.formField.svlan, true);
        this.updateDefinedAddressType();
      }
      this.onTxInterfaceChange();
    }
  }

  setDestination(): void {
    this.destinationValue.next(this.selectedDestination);
    this.twampTestService.setDestinationAgent(this.selectedDestination);
    this.onDataEmit(TwampConfigFieldMapping.SELECTED_DESTINATION, this.selectedDestination, true);
    if (this.selectedDestination) {
      this.getDestinationDeviceDetails();
      this.setSpecificDefaultFlows();
    }
  }

  //when new reflector added in edit mode the default flow should set for specific tab
  setSpecificDefaultFlows(): void {
    if (this.selectedIndex === 1 && this.editMode) {
      this.selectedDestination.forEach((device, index) => {
        const id = device.ipAddress.label;
        if (!this.agentFlowInfo[id]) {
          const flowName = 'Flow ' + (index + 1) + '-' + random(20);
          const commonFlow = cloneDeep(this.commonFlows);
          commonFlow[0].name = flowName;
          this.agentFlowInfo[id] = commonFlow;
        }
      });
      this.onOtherTabDataValueChange.emit({
        componentId: 'twamp-test-advanced.component.html',
        key: TwampConfigFieldMapping.SPECIFIC_FLOWS,
        newValue: this.agentFlowInfo,
        isValid: true
      });
    }
  }

  getDestinationDeviceDetails(): void {
    const unManagedDevice =
      this.unManagedDevices.filter(
        item =>
          this.selectedDestination.some(data => data.id === item.deviceData.id) &&
          item.deviceData?.['running-reflectors']?.length
      ) || [];
    const managedDevice =
      this.twampRunningDevices?.filter(
        item =>
          this.selectedDestination.some(data => data.id === item.deviceData.id) &&
          item.deviceData?.['running-reflectors']?.length
      ) || [];
    const destinationAgents = [...unManagedDevice, ...managedDevice];
    this.onDataEmit(TwampConfigFieldMapping.SELECTED_DESTINATION_AGENTS, destinationAgents, true);
  }

  // eslint-disable-next-line
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  updateDefinedAddressType(): void {
    if (this.formField.initiatorIp === this.ipFields.assignedIpAddress) {
      this.formField.definedAddress = definedAddressValue.MANAGEMENT_IP;
    } else if (this.formField.initiatorIp === this.ipFields.testIpAddress) {
      this.formField.definedAddress = definedAddressValue.TEST_IP;
    } else if (
      !this.ipFields.testIpAddress ||
      this.ipFields.testIpAddress === '0.0.0.0' ||
      this.ipFields.testIpAddress.match(CommonRegEx.IPV6)
    ) {
      if (this.formField.definedAddress === definedAddressValue.TEST_IP) {
        this.formField.definedAddress = definedAddressValue.MANAGEMENT_IP;
        this.definedAddressTypes[0].enabled = false;
      }
    } else {
      this.formField.definedAddress = definedAddressValue.TEST_IP;
      this.definedAddressTypes[0].enabled = true;
    }
    this.onDefinedAddressChange();
  }

  onTxInterfaceChange(): void {
    this.txTypeImage =
      this.formField.txInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? TX_IMAGE.EQUIPMENT
        : TX_IMAGE.NETWORK;
    this.formField.macAddress =
      this.formField.txInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? this.formField.equipmentMac
        : this.formField.networkMac;
    this.onMacAddressChange();
    this.onDataEmit(TwampConfigFieldMapping.TRANSMIT_INTERFACE, this.formField.txInterface, true);
  }

  onMacAddressChange(): void {
    this.onDataEmit(TwampConfigFieldMapping.MAC_ADDRESS, this.formField.macAddress, true);
  }

  onIpAddressTypeChange(): void {
    this.onDataEmit(TwampConfigFieldMapping.IP_ADDRESS_TYPE, this.formField.ipAddrType, true);
    this.addressUpdated();
  }

  addressUpdated(): void {
    this.formField.isAddressValid = false;
    if (this.formField.ipAddrType === IPAddressValue.IP_V4) {
      if (CommonRegEx.IP_REGEX.test(this.formField.initiatorIp)) {
        this.formField.isAddressValid = true;
      }
    } else {
      if (CommonRegEx.IPV6.test(this.formField.initiatorIp)) {
        this.formField.isAddressValid = true;
      }
    }
    this.onDataEmit(
      TwampConfigFieldMapping.ADDRESS,
      this.formField.initiatorIp,
      this.formField.isAddressValid
    );
    if (this.formField.ipAddrType === IPAddressValue.IP_V6) {
      this.onDataEmit(TwampConfigFieldMapping.IP_ADDRESS_TYPE, IPAddressValue.IP_V6, true);
    } else {
      this.onDataEmit(TwampConfigFieldMapping.IP_ADDRESS_TYPE, IPAddressValue.IP_V4, true);
    }
    this.gatewayUpdated();
    this.subnetUpdated();
  }

  getSubnetLabel(): string {
    if (this.formField.ipAddrType === IPAddressValue.IP_V6) {
      return 'Prefix Length';
    }
    return 'Subnet';
  }

  subnetError(type: string): string {
    if (type === 'valid') {
      return this.formField.ipAddrType === IPAddressValue.IP_V4
        ? 'Subnet is not valid'
        : 'Valid range 1-128';
    } else if (type === 'required') {
      return this.formField.ipAddrType === IPAddressValue.IP_V4
        ? 'Subnet is required'
        : 'Prefix length is required';
    }
  }
  gatewayUpdated(): void {
    this.formField.isGatewayValid = false;
    if (CommonRegEx.IP_V4_V6.test(this.formField.gatewayIp)) {
      this.formField.isGatewayValid = !this.gatewayIpValidation();
    }
    this.onDataEmit(
      TwampConfigFieldMapping.GATEWAY,
      this.formField.gatewayIp,
      this.formField.isGatewayValid
    );
    this.subnetUpdated();
  }

  validityCheck(data: number, min: number, max: number): boolean {
    if (data === 0) {
      return true;
    }
    if (data) {
      return !(data <= max && data >= min);
    }
  }
  vlanUpdated(): void {
    let valid = false;
    if (!this.validityCheck(this.formField.svlan, 1, 4095)) {
      this.formField.svlanPriority = 0;
      valid = true;
    }
    this.onDataEmit(TwampConfigFieldMapping.SVLAN_ID, this.formField.svlan, valid);
  }
  vlanPriorityUpdated(): void {
    if (!this.formField.svlanPriority) {
      this.formField.svlanPriority = 0;
    }
    const isValid = this.formField.svlanPriority >= 0 && this.formField.svlanPriority <= 7;
    this.onDataEmit(TwampConfigFieldMapping.SVLAN_PRIORITY, this.formField.svlanPriority, isValid);
  }

  onVlanConfigChange(): void {
    this.onDataEmit(TwampConfigFieldMapping.VLAN_CONFIG, this.formField.vlanConfig, true);
    this.formField.svlan = '';
    this.formField.cvlan = '';
    this.onDataEmit(TwampConfigFieldMapping.SVLAN_ID, this.formField.svlan, true);
    this.onDataEmit(TwampConfigFieldMapping.CVLAN_ID, this.formField.cvlan, true);
  }

  onSVlanChange(): void {
    this.onDataEmit(
      TwampConfigFieldMapping.SVLAN_ID,
      this.formField.svlan,
      this.form.controls['sVlanInput']?.valid
    );
  }

  onSVlanPriorityChange(): void {
    this.onDataEmit(
      TwampConfigFieldMapping.SVLAN_PRIORITY,
      this.formField.svlanPriority,
      this.form.controls['sVlanPriorityInput']?.valid
    );
  }
  onCVlanChange(): void {
    this.onDataEmit(
      TwampConfigFieldMapping.CVLAN_ID,
      this.formField.cvlan,
      this.form.controls['cVlanInput']?.valid
    );
  }

  onCVlanPriorityChange(): void {
    this.onDataEmit(
      TwampConfigFieldMapping.CVLAN_PRIORITY,
      this.formField.cvlanPriority,
      this.form.controls['cVlanPriority']?.valid
    );
  }

  onCVlanEtherTypeChange(): void {
    this.onDataEmit(TwampConfigFieldMapping.CVLAN_ETHER_TYPE, this.formField.cvlanEtherType, true);
  }

  // checkIfAgentIsQT(): void {
  //   this.isQTDevice = this.selectedOriginator?.[0].isQTAgent;
  //   if (this.isQTDevice) {
  //     this.formField.vlanConfig = 'tagged';
  //   } else {
  //     this.formField.vlanConfig = 'untagged';
  //   }
  //   this.onDataEmit(TwampConfigFieldMapping.VLAN_CONFIG, this.formField.vlanConfig, true);
  // }
  subnetUpdated(): void {
    this.formField.isSubnetValid = false;
    if (
      (this.formField.ipAddrType === IPAddressValue.IP_V4 &&
        this.formField.subnet?.match(CommonRegEx.SUBNET_MASK_REGEX)) ||
      (this.formField.ipAddrType === IPAddressValue.IP_V6 &&
        this.formField.subnet?.match(CommonRegEx.SUBNET_IPv6))
    ) {
      this.formField.isSubnetValid = !this.subnetValidation();
    }
    this.onDataEmit(
      TwampConfigFieldMapping.SUBNET,
      this.formField.subnet,
      this.formField.isSubnetValid
    );
  }

  baseUdpPortUpdated(): void {
    this.onDataEmit(
      TwampConfigFieldMapping.BASE_UDP,
      this.formField.senderUdpPort,
      this.validityInitUdpCheck(this.formField.senderUdpPort)
    );
  }

  validityReflectUdpCheck(data: number): boolean {
    return +data <= 65535 && +data >= 49152;
  }
  validityInitUdpCheck(data: number): boolean {
    return +data <= 65472 && +data >= 49152;
  }

  gatewayIpValidation(): boolean {
    if (this.formField.initiatorIp && this.formField.gatewayIp) {
      if (this.formField.ipAddrType === IPAddressValue.IP_V4) {
        return this.formField.gatewayIp.match(CommonRegEx.IPV6) ? true : false;
      } else if (this.formField.ipAddrType === IPAddressValue.IP_V6) {
        return this.formField.gatewayIp.match(CommonRegEx.IPV6) ? false : true;
      }
    }
    return false;
  }
  subnetValidation(): boolean {
    if (this.formField.subnet) {
      if (this.formField.ipAddrType === IPAddressValue.IP_V4) {
        return this.formField.subnet.match(CommonRegEx.SUBNET_IPv6) ? true : false;
      } else if (this.formField.ipAddrType === IPAddressValue.IP_V6) {
        if (isNumber(parseInt(this.formField.subnet))) {
          return parseInt(this.formField.subnet) >= 1 && parseInt(this.formField.subnet) <= 128
            ? false
            : true;
        }
        return false;
      }
    }
    return false;
  }

  roundOffValue(data: number, precision: number): number {
    return data ? +data?.toFixed(precision) : data;
  }

  ngOnDestroy(): void {
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
    if (this.deviceCache) {
      this.deviceCache.unsubscribe();
    }
  }

  onDefinedAddressChange(): void {
    if (this.formField.definedAddress === definedAddressValue.TEST_IP) {
      this.formField.initiatorIp = this.ipFields.testIpAddress;
      this.formField.subnet = this.ipFields.subnet;
      this.formField.gatewayIp = this.ipFields.gateway;
    } else if (this.formField.definedAddress === definedAddressValue.MANAGEMENT_IP) {
      this.formField.initiatorIp = this.ipFields.assignedIpAddress;
    }
    this.onDataEmit(TwampConfigFieldMapping.DEFINED_ADDRESS, this.formField.definedAddress, true);
    this.onDataEmit(TwampConfigFieldMapping.GATEWAY, this.formField.gatewayIp, true);
    this.onDataEmit(TwampConfigFieldMapping.SUBNET, this.formField.subnet, true);
    this.addressUpdated();
  }
}
